import React from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer';

//-------------------------------------
// Basic components here
//-------------------------------------
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import HeaderContainer from '../../elements/HeaderContainer';
import Disclaimer from '../../others/Disclaimer';
import Skeleton from '../../others/Skeleton/SkeletonFaresCarouselBackgroundImageMobile';
import Loading from '../../others/Loading';
import NoFeaturedPopularFaresAlert from '../../alerts/NoFeaturedPopularFaresAlert';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
//import Carousel from '../../controls/Carousel';
import Carousel from '../../controls/ElasticCarousel';
import FareItemCard from '../../items/FareItemCard';

//-------------------------------------
// Every mundo integration
//-------------------------------------
import { getFares } from '../../../_integrations/everymundo/fares';
import { getAirports } from '../../../_integrations/everymundo/airports';

import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/groupedRoutesAggregation';

import { useFilters, OriginDestinationLogic } from '../_commons/filters';
import { useFareItemProps } from '../_commons/tracking';
import InlineFilters from '../../controls/InlineFilters/InlineFiltersMobile';
import SecondaryFilters from '../../controls/SecondaryFilters/SecondaryFilters';

import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';

function FaresCarouselBackgroundImageContainerMobile(props) {
  const {
    prePopulation,
    integrationSettings,
    meta,
    pageContext,
    getAirports: getAirportsFn,
    getFares: getFaresFn,
    direction,
    faresTrasformationLogic,
    showFilters,
    initialDestinationCards,
    initialDepartureAirports,
    fareItemOnClickAction,
    filters: { availableFilters, hideBudgetOnMultiCurrency, ...filters },
    showSkeletonScreen,
    loading,
    showLoading,
    noResultsPlaceHolder,
    alert,
    showDisclaimer,
    mainContentSettings,
    disclaimerContentSettings,
    disclaimer: disclaimerSettings,
    carouselSettings,
    skeletonSettings,
    noFaresBehavior,
    theme,
    // deprecated props
    filterContentSettings, // use filters.containerSettings
    locationSelector, // use filters.originDestinationSettings
    budgetSetting, // use filters.budgetSettings
    datesRangeSelector, // use filters.dateRangeSettings
    clearAll, // use filters.clearAllSettings
    originDestinationLogic,
    secondaryFilters,
    showSecondaryFilters,
    enableSEO = true,
    hiddenSkeleton = false
  } = props;

  // Define filter props using deprecated container props (for backwards compatibility)
  // Don't rely on this props for new configurations, they will be removed in a future version
  const filtersConfig = {
    containerSettings: filterContentSettings,
    originDestinationSettings: {
      components: {
        origin: locationSelector,
        destination: locationSelector
      }
    },
    budgetSettings: budgetSetting,
    dateRangeSettings: datesRangeSelector,
    clearAllSettings: clearAll,
    ...filters
  };

  const [className, style] = useStyles(props, { direction, theme });
  const styles = Object.assign({ minHeight: '600px' }, style);

  const secondaryFiltersRef = React.useRef(null);

  const {
    fares: { match, ...fares },
    dispatch,
    retry,
    tracking,
    ...filtersState
  } = useFilters({
    originDestinationLogic,
    filter: { prePopulation, integrationSettings, meta, pageContext, getAirports: getAirportsFn },
    initialData: initialDestinationCards,
    initialOrigins: initialDepartureAirports,
    minInitialResults: noFaresBehavior === 'hidden' ? 1 : null,
    getFares: getFaresFn,
    faresTransformationLogic: faresTrasformationLogic,
    availableFilters: showFilters ? availableFilters : [],
    showSecondaryFilters,
    availableSecondaryFilter: secondaryFilters ? secondaryFilters.availableFilters : [],
    hideBudgetOnMultiCurrency,
    showOrigin:
      filtersConfig.originDestinationSettings && filtersConfig.originDestinationSettings.showOrigin,
    showDestination:
      filtersConfig.originDestinationSettings &&
      filtersConfig.originDestinationSettings.showDestination,
    hiddenSkeleton
  });

  const itemSettings = useFareItemProps(
    carouselSettings.itemSettings,
    dispatch,
    fareItemOnClickAction
  );

  let newItemsSettings = {
    ...itemSettings,
    fareItemMiles: {
      ...itemSettings.fareItemMiles,
      decimalSeparator: prePopulation?.priceFormat?.decimalSeparator
    }
  };

  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }

  const header = (
    <>
      {showLoading && match('LOADING') && <Loading {...loading} />}
      {showSkeletonScreen && match('SKELETON') && (
        <Skeleton {...skeletonSettings} position="absolute" width="100" />
      )}
      <HeaderContainer {...props} handleSeeMoreDeals={handleSeeMoreDeals} />
    </>
  );

  const renderSecondaryFilters = showSecondaryFilters && (
    <SecondaryFilters
      ref={secondaryFiltersRef}
      {...filters}
      {...filtersState}
      {...props.secondaryFilters}
      availableFilters={filtersState.availableSecondaryFilters}
      filter={{
        ...{ prePopulation, integrationSettings, meta, getAirports }
      }}
    />
  );

  return (
    <InlineFilters
      className={`${className} ${FaresCarouselBackgroundImageContainerMobile.componentName}`}
      style={styles}
      dataTestId="faresCarouselBackgroundImageContainerMobile"
      header={header}
      {...filtersConfig}
      {...filtersState}
    >
      {renderSecondaryFilters}
      <Row {...mainContentSettings} gutters>
        {match('ERROR') && (
          <Col marginTop={alert.marginTop}>
            <ContainerErrorAlert {...alert} tryAgain={retry} />
          </Col>
        )}
        {match('NO_RESULTS') && (
          <Col>
            <NoFeaturedPopularFaresAlert {...noResultsPlaceHolder} />
          </Col>
        )}
        {match('COMPLETE') && (
          <Col>
            <Carousel
              {...carouselSettings}
              itemSettings={newItemsSettings}
              ItemComponentInstance={FareItemCard}
              itemsData={fares.data}
              tracking={tracking}
            />
          </Col>
        )}
      </Row>
      {showDisclaimer && (
        <Row {...disclaimerContentSettings}>
          <Col>
            <Disclaimer
              labelKey="cmp-fares-carousel-background-image-container-disclaimer"
              {...disclaimerSettings}
            />
          </Col>
        </Row>
      )}
    </InlineFilters>
  );
}

FaresCarouselBackgroundImageContainerMobile.propTypes = {
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to budget
   */
  budgetSetting: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to carousel
   */
  carouselSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * To be used to set if the container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * Props of clearAll
   */
  clearAll: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to dates range selector
   */
  datesRangeSelector: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to destination item
   */
  destinationItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Direction of the component
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * Action to be executed when clicking in fareItem
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * Possible ways to get fares from API
   */
  faresTrasformationLogic: PropTypes.oneOf(['grouped-routes', 'agreggation']),
  /**
   * filterContentSettings props
   */
  filterContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for request to obtain airports
   */
  getAirports: PropTypes.func,
  /**
   * Function to getFares
   */
  getFares: PropTypes.func,
  /**
   * headerLocation
   */
  headerLocation: PropTypes.objectOf(PropTypes.any),
  /**
   * Used to prepopulate the component
   */
  initialDepartureAirports: PropTypes.arrayOf(PropTypes.any),
  /**
   * Used to prepopulate the component
   */
  initialDestinationCards: PropTypes.arrayOf(PropTypes.any),
  /**
   * Props to location selector
   */
  locationSelector: PropTypes.objectOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Component Behavior when there are not results available
   */
  noFaresBehavior: PropTypes.oneOf(['hidden', 'showNoResults']),
  /**
   * Props of noResultsPlaceHolder
   */
  noResultsPlaceHolder: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to other fixed title
   */
  otherPopularDestinationsLabel: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to other variable title
   */
  otherPopularDestinationsValue: PropTypes.objectOf(PropTypes.any),
  /**
   * pageContext settings
   */
  pageContext: PropTypes.objectOf(PropTypes.any),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Show Airport Iata Code in locations
   */
  showAirportIataCode: PropTypes.bool,
  /**
   * Determine if the component will show the disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * Determine if the component will show the filters
   */
  showFilters: PropTypes.bool,
  /**
   * Shows Loading Component
   */
  showLoading: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Title of the component
   */
  titleKey: PropTypes.string,
  /**
   * Title Component
   */
  titleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to translate
   */
  translate: PropTypes.func,
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  headerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * All integration configurations
   */
  meta: PropTypes.objectOf(PropTypes.any),
  /**
   * Login to load origin and destination airports
   * same: Origin and Destination are pre-populated or not
   *      a single airport service call is done to fill out the Origin
   *      and Destinations filters (for performance purposes).
   * different: Origin and Destination are pre-populated or not
   *      2 airports service calls are done to fill out the Origin
   *      and Destinations filters.
   */
  originDestinationLogic: PropTypes.oneOf([
    OriginDestinationLogic.SAME,
    OriginDestinationLogic.DIFFERENT
  ]),
  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Show the HeaderLink Component
   */
  showHeaderLink: PropTypes.bool,
  /**
   * headerLinkKey of the component
   */
  headerLinkKey: PropTypes.string,
  /**
   * headerLinkSettings props
   */
  headerLinkSettings: PropTypes.shape({
    defaultUrl: PropTypes.string,
    urls: PropTypes.object,
    container: PropTypes.any
  })
};

FaresCarouselBackgroundImageContainerMobile.defaultProps = {
  fareItemInteractionType: 'popup',
  alert: {
    bgColor: 'light',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded',
    labelKeyTitle: 'cmp-container-error-message',
    labelKeySubTitle: 'cmp-container-error-reload-deals',
    marginTop: '0'
  },
  budgetSetting: {},
  carouselSettings: {},
  containerType: 'stateful',
  clearAll: {
    color: 'link',
    bgColor: 'transparent',
    borderNone: true
  },
  datesRangeSelector: {},
  destinationItem: {},
  direction: 'ltr',
  disclaimer: {},
  disclaimerContentSettings: {
    marginTop: '2'
  },
  filters: {
    availableFilters: ['OriginDestination', 'SingleDateRangePicker', 'Budget']
  },
  fareItemOnClickAction: null,
  faresTrasformationLogic: 'grouped-routes',
  filterContentSettings: {
    marginTop: '1'
  },
  getAirports,
  getFares,
  headerLocation: {},
  initialDepartureAirports: null,
  initialDestinationCards: null,
  locationSelector: {},
  loading: {},
  mainContentSettings: {
    marginTop: '2'
  },
  noFaresBehavior: 'hidden',
  noResultsPlaceHolder: {
    bgColor: 'light',
    marginTop: '0',
    paddingWidth: '1',
    rounded: '@theme:card.rounded'
  },
  otherPopularDestinationsLabel: {},
  otherPopularDestinationsValue: {},
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  prePopulation: prePopulationDefaultProps,
  showAirportIataCode: true,
  showDisclaimer: true,
  showFilters: true,
  showLoading: true,
  showSkeletonScreen: true,
  skeletonSettings: {
    style: { backgroundColor: 'white' }
  },
  titleKey: 'cmp-fares-carousel-background-image-container-title',
  localizationData: null,
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'faresCarouselBackgroundImage'
  },
  theme: null,
  integrationSettings: {},
  meta: null,
  originDestinationLogic: OriginDestinationLogic.SAME,
  subTitleKey: 'cmp-fares-carousel-sub-title',
  headerLinkKey: 'cmp-fares-carousel-header-link-mobile'
};
FaresCarouselBackgroundImageContainerMobile.componentName =
  'FaresCarouselBackgroundImageContainerMobile';

// HOC with common functionality to all containers
export default withCommonEMContainer(FaresCarouselBackgroundImageContainerMobile);
